import { useState } from 'react';
import type { WiringDiagram, MermaidDiagram } from '../../../services/api';
import type { ProjectDetailContentProps, TabId, CodingProject } from './types';
import { SafetyTab } from '@/components/Safety';
import ProjectReferencesPanel from '../../reference/ProjectReferencesPanel';
import { Button } from '@/components/ui';

import { OverviewTab } from './OverviewTab';
import { DocumentsTab } from './DocumentsTab';
import { PlansTab } from './PlansTab';
import { DiagramsTab } from './DiagramsTab';
import { FlowDiagramsTab } from './FlowDiagramsTab';
import { CodingTab } from './CodingTab';
import { HistoryTab } from './HistoryTab';
import { SettingsTab } from './SettingsTab';

export function ProjectDetailContent({
  project,
  onOpenDocument,
  onOpenDiagram,
  onOpenFlowDiagram,
  onOpenCodingProject,
  onEditProject,
  onRefreshProject,
  onDeleteProject,
}: ProjectDetailContentProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Shared state lifted to orchestrator so tab-bar counts stay in sync
  const [diagrams, setDiagrams] = useState<WiringDiagram[]>([]);
  const [flowDiagrams, setFlowDiagrams] = useState<MermaidDiagram[]>([]);
  const [codingProjects, setCodingProjects] = useState<CodingProject[]>([]);

  const docCount = project.documents?.length || 0;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-foreground truncate">
              {project.name}
            </h2>
            {project.projectCode && (
              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-info-light text-info rounded-full font-mono">
                {project.projectCode}
              </span>
            )}
          </div>
          {project.projectTitle && (
            <p className="text-xs text-muted-foreground truncate">
              {project.projectTitle}
            </p>
          )}
        </div>
        {onEditProject && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onEditProject}
          >
            Edit
          </Button>
        )}
      </div>
      
      {/* Tab bar */}
      <div className="flex-shrink-0">
        <div className="flex gap-1 px-4">
          {([
            { id: 'overview' as TabId, label: 'Overview' },
            { id: 'documents' as TabId, label: `Documents (${docCount})` },
            { id: 'plans' as TabId, label: 'Plans' },
            { id: 'references' as TabId, label: 'References' },
            { id: 'diagrams' as TabId, label: `Wiring (${diagrams.length})` },
            { id: 'flow-diagrams' as TabId, label: `Flow (${flowDiagrams.length})` },
            { id: 'coding' as TabId, label: `Coding (${codingProjects.length})` },
            { id: 'safety' as TabId, label: 'Safety' },
            { id: 'history' as TabId, label: 'History' },
            { id: 'settings' as TabId, label: 'Settings' },
          ]).map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant="ghost"
              size="sm"
              className={`py-2.5 px-4 ${
                activeTab === tab.id
                  ? 'bg-muted/50 text-info'
                  : 'text-muted-foreground'
              }`}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'overview' && (
          <OverviewTab
            project={project}
            onOpenDocument={onOpenDocument}
            setActiveTab={setActiveTab}
          />
        )}
        
        {activeTab === 'documents' && (
          <DocumentsTab
            project={project}
            onOpenDocument={onOpenDocument}
            onRefreshProject={onRefreshProject}
          />
        )}
        
        {activeTab === 'plans' && (
          <PlansTab
            projectId={project.id}
            onOpenDocument={onOpenDocument}
          />
        )}
        
        {activeTab === 'references' && (
          <div className="space-y-4">
            <ProjectReferencesPanel 
              projectId={project.id} 
              projectName={project.name}
            />
          </div>
        )}
        
        {activeTab === 'diagrams' && (
          <DiagramsTab
            projectId={project.id}
            onOpenDiagram={onOpenDiagram}
            diagrams={diagrams}
            setDiagrams={setDiagrams}
          />
        )}
        
        {activeTab === 'flow-diagrams' && (
          <FlowDiagramsTab
            projectId={project.id}
            onOpenFlowDiagram={onOpenFlowDiagram}
            flowDiagrams={flowDiagrams}
            setFlowDiagrams={setFlowDiagrams}
          />
        )}
        
        {activeTab === 'coding' && (
          <CodingTab
            projectId={project.id}
            onOpenCodingProject={onOpenCodingProject}
            codingProjects={codingProjects}
            setCodingProjects={setCodingProjects}
          />
        )}
        
        {activeTab === 'safety' && (
          <SafetyTab projectId={project.id} />
        )}
        
        {activeTab === 'history' && (
          <HistoryTab projectId={project.id} />
        )}
        
        {activeTab === 'settings' && (
          <SettingsTab
            project={project}
            onEditProject={onEditProject}
            onDeleteProject={onDeleteProject}
          />
        )}
      </div>
    </div>
  );
}
